/* The client portal's accent, shared by the portal (which fills its pills
   with it) and the app (which previews it and edits it). Pure functions and
   no React, so the portal can import this subpath without the component
   library. */

/** The accent a firm gets before it picks one. The portal's stylesheet
    defaults --firm-accent to the same hex. */
export const DEFAULT_FIRM_ACCENT = "#537c63"

type Rgb = readonly [number, number, number]

const WHITE: Rgb = [255, 255, 255]
const NEAR_BLACK: Rgb = [23, 23, 25]
/* The dark green every accent is mixed toward before it fills a pill, so a
   pale brand colour never sits at 2:1 on paper. */
const SOLID_BASE: Rgb = [38, 58, 45]

const readHex = (hex: string): Rgb => [
  Number.parseInt(hex.slice(1, 3), 16),
  Number.parseInt(hex.slice(3, 5), 16),
  Number.parseInt(hex.slice(5, 7), 16),
]

const toHex = (rgb: Rgb) =>
  `#${rgb.map((channel) => channel.toString(16).padStart(2, "0")).join("")}`

const channelLuminance = (value: number) => {
  const channel = value / 255
  return channel <= 0.04045
    ? channel / 12.92
    : Math.pow((channel + 0.055) / 1.055, 2.4)
}

const luminance = (rgb: Rgb) =>
  0.2126 * channelLuminance(rgb[0]) +
  0.7152 * channelLuminance(rgb[1]) +
  0.0722 * channelLuminance(rgb[2])

const contrast = (a: Rgb, b: Rgb) => {
  const first = luminance(a)
  const second = luminance(b)
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05)
}

const mix = (firm: Rgb, firmShare: number): Rgb => [
  Math.round(firm[0] * firmShare + SOLID_BASE[0] * (1 - firmShare)),
  Math.round(firm[1] * firmShare + SOLID_BASE[1] * (1 - firmShare)),
  Math.round(firm[2] * firmShare + SOLID_BASE[2] * (1 - firmShare)),
]

const bestContrast = (solid: Rgb) =>
  Math.max(contrast(WHITE, solid), contrast(NEAR_BLACK, solid))

/** The firm's hex as a readable pill fill: mixed 88% with a dark green, then
    darkened further in 4% steps until white or near-black text on it reads
    at 4.5:1. Most accents need no extra step; a mid-tone such as #668e66
    tops out at 4.25:1 at 88% and needs two. */
export function firmAccentSolid(hex: string): string {
  const firm = readHex(hex)
  let share = 0.88
  let solid = mix(firm, share)
  while (bestContrast(solid) < 4.5 && share > 0) {
    share = Math.max(0, share - 0.04)
    solid = mix(firm, share)
  }
  return toHex(solid)
}

/** The text colour on the accent pill: whichever of white and near-black
    reads better against the solid fill. */
export function firmAccentForeground(hex: string): string {
  const solid = readHex(firmAccentSolid(hex))
  return contrast(WHITE, solid) >= contrast(NEAR_BLACK, solid)
    ? "#ffffff"
    : "#171719"
}

/** True when the firm's colour had to be darkened past the standard mix to
    keep the pill text readable, so the setup screen can say so. */
export function firmAccentWasDarkened(hex: string): boolean {
  return firmAccentSolid(hex) !== toHex(mix(readHex(hex), 0.88))
}
